// Admin — Supplier Catalogue Access.
//
// A simple grant/revoke matrix: tenants (rows) × catalogue suppliers (columns).
// A cell is ON when the tenant has been granted that supplier's catalogue, which
// makes the supplier's products visible in that tenant's Quote Builder. Pure
// opt-in: nothing is visible until granted here.
//
// Columns are DYNAMIC — every supplier with rows in catalogue_products appears
// automatically (GET /api/admin/catalogue-access), so a newly imported catalogue
// shows up here with no code change.
//
// Backend:
//   GET  /api/admin/catalogue-access  → { tenants, suppliers, grants }
//   POST /api/admin/catalogue-access  → { retailerId, supplierId, granted }

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

interface Tenant { id: string; name: string | null; slug: string | null }
interface Supplier { id: string; slug: string; name: string; productCount: number; doorRangeCount: number }
interface Grant { retailerId: string; supplierId: string; quoteBuilder: boolean; wizard: boolean }
/** A supplier with door ranges but no PG `suppliers` row — cannot be granted. */
interface Ungrantable { slug: string; doorRangeCount: number }
interface AccessResponse {
  ok: boolean;
  tenants: Tenant[];
  suppliers: Supplier[];
  grants: Grant[];
  ungrantable?: Ungrantable[];
}

/** The two independent surfaces a grant can apply to. */
type Surface = "quoteBuilder" | "wizard";
const SURFACES: readonly { key: Surface; short: string; title: string }[] = [
  { key: "quoteBuilder", short: "QB", title: "Quote Builder — retailer can quote from this supplier's trade catalogue" },
  { key: "wizard",       short: "Wiz", title: "Lead-gen wizard — this supplier's door ranges are shown to the retailer's customers" },
];

const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const isAbort = (e: unknown): boolean => e instanceof DOMException && e.name === "AbortError";

const STYLE = `
.sca-page{padding:24px;max-width:1100px;margin:0 auto}
.sca-header h1{margin:0 0 4px;font-size:22px;font-weight:700}
.sca-header p{margin:0 0 20px;color:#64748b;font-size:14px}
.sca-tablewrap{overflow-x:auto;border:1px solid #e2e8f0;border-radius:12px;background:#fff}
.sca-table{border-collapse:collapse;width:100%;font-size:14px}
.sca-table th,.sca-table td{padding:12px 16px;text-align:left;border-bottom:1px solid #f1f5f9}
.sca-table thead th{background:#f8fafc;font-weight:600;white-space:nowrap}
.sca-table th.sca-supcol{text-align:center}
.sca-supcol small{display:block;color:#94a3b8;font-weight:400}
.sca-table td.sca-cell{text-align:center}
.sca-tenant{font-weight:600}.sca-tenant small{display:block;color:#94a3b8;font-weight:400}
.sca-sw{position:relative;display:inline-block;width:40px;height:22px;cursor:pointer}
.sca-sw input{opacity:0;width:0;height:0}
.sca-sw .sca-track{position:absolute;inset:0;background:#cbd5e1;border-radius:999px;transition:background .15s}
.sca-sw .sca-track::after{content:"";position:absolute;top:2px;left:2px;width:18px;height:18px;background:#fff;border-radius:50%;transition:transform .15s}
.sca-sw input:checked + .sca-track{background:#16a34a}
.sca-sw input:checked + .sca-track::after{transform:translateX(18px)}
.sca-sw input:disabled + .sca-track{opacity:.5}
.sca-empty{padding:40px;text-align:center;color:#94a3b8}
.sca-loading{padding:40px;text-align:center;color:#94a3b8}
/* Two surfaces per cell — the switch pair reads as one unit per supplier. */
.sca-surfaces{display:flex;gap:14px;justify-content:center;align-items:flex-start}
.sca-surface{display:flex;flex-direction:column;align-items:center;gap:4px}
.sca-surface span.sca-slabel{font-size:10px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#94a3b8}
.sca-legend{margin:0 0 16px;font-size:13px;color:#64748b}
.sca-legend b{color:#334155}
.sca-warn{margin:0 0 16px;padding:12px 14px;border:1px solid #fcd34d;background:#fffbeb;border-radius:8px;font-size:13px;color:#92400e}
.sca-none{font-size:12px;color:#cbd5e1}
`;

const catalogueAccessPage: Page = {
  name: "catalogue-access",
  label: "Catalogue Access",
  icon: "🔐",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let data: AccessResponse | null = null;
    let loading = true;
    // `${retailerId}|${supplierId}|${surface}` — one entry per enabled surface,
    // so the two surfaces of a cell are tracked independently.
    const granted = new Set<string>();
    const pending = new Set<string>(); // switches with an in-flight toggle

    const root = document.createElement("section");
    root.className = "sca-page";
    const styleEl = document.createElement("style");
    styleEl.textContent = STYLE;
    root.appendChild(styleEl);
    const body = document.createElement("div");
    root.appendChild(body);
    container.appendChild(root);

    const key = (r: string, s: string, surface: Surface): string => `${r}|${s}|${surface}`;

    function render(): void {
      if (loading) { body.innerHTML = `<div class="sca-loading">Loading catalogue access…</div>`; return; }
      if (!data) { body.innerHTML = `<div class="sca-empty">Couldn't load catalogue access.</div>`; return; }
      const { tenants, suppliers } = data;

      if (!suppliers.length) {
        body.innerHTML = `<header class="sca-header"><h1>Supplier Access</h1>
          <p>Choose which suppliers apply to each retailer, per surface.</p></header>
          <div class="sca-empty">No suppliers with anything to grant yet. Import a catalogue or add door ranges — the supplier will appear here automatically.</div>`;
        return;
      }
      if (!tenants.length) {
        body.innerHTML = `<header class="sca-header"><h1>Catalogue Access</h1></header>
          <div class="sca-empty">No tenants yet.</div>`;
        return;
      }

      const head = suppliers.map((s) => {
        // Say what there actually IS to grant — a supplier can have trade
        // products, door ranges, or both, and the two switches below mean
        // nothing without that context.
        const bits: string[] = [];
        if (s.productCount > 0)   bits.push(`${s.productCount.toLocaleString()} products`);
        if (s.doorRangeCount > 0) bits.push(`${s.doorRangeCount} door ${s.doorRangeCount === 1 ? "range" : "ranges"}`);
        return `<th class="sca-supcol">${esc(s.name)}<small>${bits.join(" · ")}</small></th>`;
      }).join("");

      const rows = tenants.map((t) => {
        const cells = suppliers.map((s) => {
          const switches = SURFACES.map((surf) => {
            // A surface with nothing behind it would be a dead switch, so don't
            // offer it: no trade products means nothing to quote from, and no
            // door ranges means nothing for the wizard to show.
            const available = surf.key === "quoteBuilder" ? s.productCount > 0 : s.doorRangeCount > 0;
            if (!available) {
              return `<div class="sca-surface"><span class="sca-none">—</span>
                <span class="sca-slabel">${esc(surf.short)}</span></div>`;
            }
            const k = key(t.id, s.id, surf.key);
            const on = granted.has(k);
            const busy = pending.has(k);
            return `<div class="sca-surface">
              <label class="sca-sw" title="${esc(surf.title)}">
                <input type="checkbox" data-retailer="${esc(t.id)}" data-supplier="${esc(s.id)}"
                       data-surface="${esc(surf.key)}" ${on ? "checked" : ""} ${busy ? "disabled" : ""} />
                <span class="sca-track" aria-hidden="true"></span>
              </label>
              <span class="sca-slabel">${esc(surf.short)}</span>
            </div>`;
          }).join("");
          return `<td class="sca-cell"><div class="sca-surfaces">${switches}</div></td>`;
        }).join("");
        return `<tr><td class="sca-tenant">${esc(t.name || t.slug || t.id)}${t.slug ? `<small>${esc(t.slug)}</small>` : ""}</td>${cells}</tr>`;
      }).join("");

      // The two surfaces default in OPPOSITE directions, which is surprising
      // unless it is spelled out on the page itself.
      const warn = (data.ungrantable || []).length
        ? `<div class="sca-warn"><b>Not grantable yet:</b> ${
            (data.ungrantable || []).map((u) => `${esc(u.slug)} (${u.doorRangeCount} door ranges)`).join(", ")
          }. These have door ranges but no supplier record, so they cannot be assigned to a retailer. Add them on the Suppliers page first.</div>`
        : "";

      body.innerHTML = `
        <header class="sca-header">
          <h1>Supplier Access</h1>
          <p>Choose which suppliers apply to each retailer. The two switches are independent — a supplier you quote from is not necessarily one you show customers.</p>
        </header>
        ${warn}
        <p class="sca-legend">
          <b>QB — Quote Builder.</b> Off by default: a retailer sees a trade catalogue only if switched on here.<br>
          <b>Wiz — Lead-gen wizard.</b> With <em>no</em> wizard switches on for a retailer, their wizard shows <b>all</b> published door ranges. Switch any on and they see exactly that selection.
        </p>
        <div class="sca-tablewrap">
          <table class="sca-table">
            <thead><tr><th>Tenant</th>${head}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }

    async function toggle(
      retailerId: string, supplierId: string, surface: Surface, want: boolean,
    ): Promise<void> {
      const k = key(retailerId, supplierId, surface);
      pending.add(k);
      render();
      try {
        // The server returns the row's resulting state for BOTH surfaces, so
        // trust that rather than assuming — it also deletes the row when the
        // last surface goes off.
        const resp = await ctx.api<{ ok: boolean; state?: { quoteBuilder: boolean; wizard: boolean } }>(
          "/admin/catalogue-access",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({ retailerId, supplierId, surface, granted: want }),
          },
        );
        const state = resp.state ?? { quoteBuilder: false, wizard: false };
        for (const surf of SURFACES) {
          const sk = key(retailerId, supplierId, surf.key);
          if (state[surf.key]) granted.add(sk); else granted.delete(sk);
        }
        const label = surface === "wizard" ? "Wizard" : "Quote Builder";
        ctx.showToast(`${label} access ${want ? "granted" : "revoked"}`, "success");
      } catch (err) {
        if (isAbort(err)) return;
        ctx.showToast(err instanceof Error ? err.message : "Update failed", "error");
      } finally {
        pending.delete(k);
        render();
      }
    }

    body.addEventListener("change", (e) => {
      const t = e.target as HTMLElement | null;
      if (!(t instanceof HTMLInputElement) || t.type !== "checkbox") return;
      const retailerId = t.dataset["retailer"];
      const supplierId = t.dataset["supplier"];
      const surface = t.dataset["surface"] as Surface | undefined;
      if (!retailerId || !supplierId || !surface) return;
      void toggle(retailerId, supplierId, surface, t.checked);
    });

    void (async () => {
      try {
        data = await ctx.api<AccessResponse>("/admin/catalogue-access", { method: "GET", signal: ac.signal });
        granted.clear();
        (data.grants || []).forEach((g) => {
          if (g.quoteBuilder) granted.add(key(g.retailerId, g.supplierId, "quoteBuilder"));
          if (g.wizard)       granted.add(key(g.retailerId, g.supplierId, "wizard"));
        });
      } catch (err) {
        if (!isAbort(err)) data = null;
      } finally {
        loading = false;
        render();
      }
    })();

    render();
    return () => { ac.abort(); };
  },
};

registerPage(catalogueAccessPage);

export default catalogueAccessPage;
